import { createSlice } from '@reduxjs/toolkit'
import {
  IEventPostItem,
  IResponseGetEventPostDetail,
  IResponseIncomeEvent,
  IResponseRevenueEvent,
  ITicket,
  ITicketResponse
} from 'src/interfaces/event-posts'
import { RootState } from '..'
import {
  createEventPostAction,
  deleteEventPostAction,
  getAllEventAuthorsAction,
  getEventPostDetailAction,
  getIncomeEvent,
  getListEventPostsAction,
  getListTicket,
  getRevenueEvent,
  updateEventPostAction,
  uploadEventThumbnailAction,
} from '../actions/event-posts'

interface IEventPostsState {
  eventPosts: IEventPostItem[]
  totalEventPosts: number
  pageEventPosts: number | string
  limitEventPosts: number | string
  selectedEventPost?: IResponseGetEventPostDetail
  loadings: Record<string, boolean | undefined>
  dataRevenuEvent: IResponseRevenueEvent | null
  dataIncomeEvent: IResponseIncomeEvent | null
  dataListTicket: ITicketResponse[]
}

const initialState: IEventPostsState = {
  eventPosts: [],
  totalEventPosts: 0,
  pageEventPosts: 0,
  limitEventPosts: 0,
  selectedEventPost: {} as IResponseGetEventPostDetail,
  loadings: {},
  dataRevenuEvent: null,
  dataIncomeEvent: null,
  dataListTicket: []
}

const eventPostsSlice = createSlice({
  name: 'eventPosts',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get List Event Posts
    builder.addCase(getListEventPostsAction.pending, (state) => {
      state.loadings[`getListEventPostsAction`] = true
    })
    builder.addCase(getListEventPostsAction.fulfilled, (state, action) => {
      state.loadings[`getListEventPostsAction`] = false
      const payload = action.payload as any
      state.eventPosts = payload.data.items
      state.totalEventPosts = payload.data.total
      state.pageEventPosts = payload.data.page
      state.limitEventPosts = payload.data.limit
    })
    builder.addCase(getListEventPostsAction.rejected, (state) => {
      state.loadings[`getListEventPostsAction`] = false
    })

    // Get Event Post Detail
    builder.addCase(getEventPostDetailAction.pending, (state) => {
      state.loadings[`getEventPostDetailAction`] = true
    })
    builder.addCase(getEventPostDetailAction.fulfilled, (state, action) => {
      state.loadings[`getEventPostDetailAction`] = false
      state.selectedEventPost = action.payload.data
    })
    builder.addCase(getEventPostDetailAction.rejected, (state) => {
      state.loadings[`getEventPostDetailAction`] = false
    })

    // Create Event Post
    builder.addCase(createEventPostAction.pending, (state) => {
      state.loadings[`createEventPostAction`] = true
    })
    builder.addCase(createEventPostAction.fulfilled, (state, action) => {
      state.loadings[`createEventPostAction`] = false
    })
    builder.addCase(createEventPostAction.rejected, (state) => {
      state.loadings[`createEventPostAction`] = false
    })

    // Update Event Post
    builder.addCase(updateEventPostAction.pending, (state) => {
      state.loadings[`updateEventPostAction`] = true
    })
    builder.addCase(updateEventPostAction.fulfilled, (state, action) => {
      state.loadings[`updateEventPostAction`] = false
    })
    builder.addCase(updateEventPostAction.rejected, (state) => {
      state.loadings[`updateEventPostAction`] = false
    })

    // Delete Event Post
    builder.addCase(deleteEventPostAction.pending, (state) => {
      state.loadings[`deleteEventPostAction`] = true
    })
    builder.addCase(deleteEventPostAction.fulfilled, (state, action) => {
      state.loadings[`deleteEventPostAction`] = false
    })
    builder.addCase(deleteEventPostAction.rejected, (state) => {
      state.loadings[`deleteEventPostAction`] = false
    })

    // Get All Event Authors
    builder.addCase(getAllEventAuthorsAction.pending, (state) => {
      state.loadings[`getAllEventAuthorsAction`] = true
    })
    builder.addCase(getAllEventAuthorsAction.fulfilled, (state, action) => {
      state.loadings[`getAllEventAuthorsAction`] = false
    })
    builder.addCase(getAllEventAuthorsAction.rejected, (state) => {
      state.loadings[`getAllEventAuthorsAction`] = false
    })

    // Upload Event Thumbnail
    builder.addCase(uploadEventThumbnailAction.pending, (state) => {
      state.loadings[`uploadEventThumbnailAction`] = true
    })
    builder.addCase(uploadEventThumbnailAction.fulfilled, (state, action) => {
      state.loadings[`uploadEventThumbnailAction`] = false
    })
    builder.addCase(uploadEventThumbnailAction.rejected, (state) => {
      state.loadings[`uploadEventThumbnailAction`] = false
    })

    // Get Rewvenue Event
    builder.addCase(getRevenueEvent.pending, (state) => {
      state.loadings[`getRevenueEvent`] = true
    })
    builder.addCase(getRevenueEvent.fulfilled, (state, action) => {
      state.loadings[`getRevenueEvent`] = false
      state.dataRevenuEvent = action.payload.data as IResponseRevenueEvent
    })
    builder.addCase(getRevenueEvent.rejected, (state) => {
      state.loadings[`getRevenueEvent`] = false
    })

    // Get Incoming Event
    builder.addCase(getIncomeEvent.pending, (state) => {
      state.loadings[`getIncomeEvent`] = true
    })
    builder.addCase(getIncomeEvent.fulfilled, (state, action) => {
      state.loadings[`getIncomeEvent`] = false
      state.dataIncomeEvent = action.payload.data as IResponseIncomeEvent
    })
    builder.addCase(getIncomeEvent.rejected, (state) => {
      state.loadings[`getIncomeEvent`] = false
    })

    // Get List Ticket
    builder.addCase(getListTicket.pending, (state) => {
      state.loadings[`getListTicket`] = true
    })
    builder.addCase(getListTicket.fulfilled, (state, action) => {
      state.loadings[`getListTicket`] = false
      state.dataListTicket.push(action.payload.data as ITicketResponse) 
    })
    builder.addCase(getListTicket.rejected, (state) => {
      state.loadings[`getListTicket`] = false
    })
  },
})

export const eventPostsActions = {
  ...eventPostsSlice.actions,
}

export const selectEventPostsLoading = (state: RootState, name: string) =>
  state.eventPosts.loadings[`${name}`]

export default eventPostsSlice.reducer
